import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { initializeAuth } from '../store/authSlice'
import { showInitialLoader, hideInitialLoaderImmediate } from '../utils/initialLoaderManager'
import { authAPI } from '../utils/supabase'

export default function PublicRoute({ children, allowAuthenticatedAccess = false }) {
  const dispatch = useDispatch()
  const location = useLocation()
  const { isAuthenticated, initialized } = useSelector((state) => state.auth)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)
  const [checkingRecovery, setCheckingRecovery] = useState(true)

  useEffect(() => {
    // Initialize auth state on app start
    if (!initialized) {
      dispatch(initializeAuth())
    }

    // NOTE: Removed auth state listener to prevent bypassing MFA checks
    // All authentication state changes are now handled by ProtectedRoute's MFA-aware flow
  }, [dispatch, initialized])

  // Listen for PASSWORD_RECOVERY event from Supabase
  // This fires when user clicks the reset password link in their email
  useEffect(() => {
    const { data: { subscription } } = authAPI.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true)
      }
      setCheckingRecovery(false)
    })

    // Also check immediately in case event already fired or won't fire
    // Give a brief moment for the event to potentially fire
    const timeout = setTimeout(() => {
      setCheckingRecovery(false)
    }, 300)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeout)
    }
  }, [])

  // Show loading while checking auth state or recovery status
  // Only wait for initialization (not shared loading state which is affected by other auth operations)
  // Skip recovery check delay if already on reset-password page (user arrived directly)
  const isOnResetPasswordPage = location.pathname === '/reset-password'
  if (!initialized || (checkingRecovery && !isOnResetPasswordPage)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4 py-8 font-system light-mode-only">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="apple-card animate-slide-in px-5 py-8">
            <div className="text-center space-y-4">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="apple-text-body text-text-muted">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // If PASSWORD_RECOVERY event detected and not already on reset-password page, redirect there
  if (isPasswordRecovery && location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password" replace />
  }

  // For signup confirmation, allow authenticated users if they have email in state
  // This prevents race condition where signup completes before navigation
  const isSignupConfirmation = location.pathname === '/signup/confirm' && location.state?.email

  // Allow access to reset-password page even if authenticated (recovery session)
  const isResetPasswordPage = location.pathname === '/reset-password'

  // Redirect to dashboard if already authenticated (unless special case like signup confirmation or password reset)
  if (isAuthenticated && !isSignupConfirmation && !isResetPasswordPage && !allowAuthenticatedAccess) {
    // Show initial loader during redirect to board
    showInitialLoader()
    return <Navigate to="/board" replace />
  }

  // Hide initial loader for public pages (it may still be visible from initial page load)
  hideInitialLoaderImmediate()

  return children
}
